import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '@/src/utils/constants';
import { SearchIcon, HeartIcon, MapIcon, XIcon, LoaderIcon } from '@/src/components/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/src/components/ui/select';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/src/components/ui/input-group';
import PropertyCard from './PropertyCard';
import PropertyMap from './PropertyMap';

const FavoritesView = ({ 
  favorites, 
  properties, 
  onPropertyClick, 
  toggleFavorite, 
  isFavorite, 
  onBack, 
  onClearAll,
  getPropertyTags,
  onOpenTagManager,
  hasNote
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [loadingMissingFavorites, setLoadingMissingFavorites] = useState(false);
  const [allFavoriteProperties, setAllFavoriteProperties] = useState([]);
  
  const fetchMissingFavorites = async (missingIds) => {
    if (missingIds.length === 0) return [];

    setLoadingMissingFavorites(true);

    try {
      const chunkSize = 40;
      const maxConcurrent = 5;
      const chunks = [];

      for (let i = 0; i < missingIds.length; i += chunkSize) {
        chunks.push(missingIds.slice(i, i + chunkSize));
      }

      const allMissingProperties = [];

      for (let i = 0; i < chunks.length; i += maxConcurrent) {
        const batch = chunks.slice(i, i + maxConcurrent);
        const results = await Promise.all(
          batch.map(async (chunk) => {
            const rollNumbersList = chunk.map(id => `'${id}'`).join(',');
            const query = `${API_BASE_URL}?$where=${encodeURIComponent(`roll_number IN (${rollNumbersList})`)}&$limit=${chunk.length}`;

            const response = await fetch(query);
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
        );
        allMissingProperties.push(...results.flat());
      }

      console.log(`Successfully loaded ${allMissingProperties.length} missing favorite properties`);
      return allMissingProperties;
    } catch (error) {
      console.error('Error fetching missing favorites:', error);
      return [];
    } finally {
      setLoadingMissingFavorites(false);
    }
  };
  
  useEffect(() => {
    const loadedFavoriteIds = properties.map(p => p.roll_number).filter(id => favorites.includes(id));
    const missingFavoriteIds = favorites.filter(id => !loadedFavoriteIds.includes(id));
    
    if (missingFavoriteIds.length > 0) {
      fetchMissingFavorites(missingFavoriteIds).then(missingProperties => {
        const combinedProperties = [...properties, ...missingProperties];
        const uniqueProperties = Array.from(
          new Map(combinedProperties.map(p => [p.roll_number, p])).values()
        );
        setAllFavoriteProperties(uniqueProperties.filter(property => 
          favorites.includes(property.roll_number)
        ));
      });
    } else {
      setAllFavoriteProperties(properties.filter(property => 
        favorites.includes(property.roll_number)
      ));
    }
  }, [favorites, properties]);
  
  const getFavoriteProperties = () => {
    return {
      loaded: allFavoriteProperties,
      missing: []
    };
  };
  
  const getFilteredAndSortedFavorites = () => {
    let favoriteData = getFavoriteProperties();
    let favoriteProperties = favoriteData.loaded;
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      favoriteProperties = favoriteProperties.filter(property => 
        property.full_address?.toLowerCase().includes(term) ||
        property.neighbourhood_area?.toLowerCase().includes(term) ||
        property.roll_number?.toString().includes(term)
      );
    }
    
    favoriteProperties.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return favorites.indexOf(a.roll_number) - favorites.indexOf(b.roll_number);
        case 'oldest':
          return favorites.indexOf(b.roll_number) - favorites.indexOf(a.roll_number);
        case 'price-high':
          return (b.total_assessed_value || 0) - (a.total_assessed_value || 0);
        case 'price-low':
          return (a.total_assessed_value || 0) - (b.total_assessed_value || 0);
        case 'address':
          return (a.full_address || '').localeCompare(b.full_address || '');
        default:
          return 0;
      }
    });
    
    return {
      properties: favoriteProperties,
      missing: favoriteData.missing
    };
  };
  
  const filteredFavoritesData = getFilteredAndSortedFavorites();
  const filteredFavorites = filteredFavoritesData.properties;
  const missingFavorites = filteredFavoritesData.missing;
  
  return (
    <div className="min-h-screen p-4">
      <div className="max-w-7xl mx-auto">
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <Button onClick={onBack} variant="outline" className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                  <polyline points="15,18 9,12 15,6"></polyline>
                </svg>
                Back to Search
              </Button>
              <div className="flex items-center gap-3">
                {favorites.length > 0 && (
                  <Button 
                    onClick={onClearAll} 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Clear All Favorites
                  </Button>
                )}
              </div>
            </div>
            
            <div className="text-center">
              <h1 className="text-4xl font-semibold text-foreground mb-2 flex items-center justify-center gap-3">
                <HeartIcon filled={true} className="w-8 h-8 text-red-500" />
                My Favorite Properties
              </h1>
              <p className="text-muted-foreground text-lg">
                {favorites.length} {favorites.length === 1 ? 'property' : 'properties'} saved to your favorites
              </p>
            </div>
          </CardContent>
        </Card>

        {favorites.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 text-gray-300 mx-auto mb-6">
                <HeartIcon className="w-20 h-20" />
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">No Favorite Properties Yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Start exploring properties and click the heart icon on any property you're interested in to add it to your favorites.
              </p>
              <Button onClick={onBack} size="lg">
                <SearchIcon className="w-5 h-5 mr-2" />
                Start Searching Properties
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div>
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                  <div className="flex-1 max-w-md">
                    <InputGroup>
                      <InputGroupAddon>
                        <SearchIcon className="w-4 h-4" />
                      </InputGroupAddon>
                      <InputGroupInput
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search your favorites..."
                        className="h-10"
                      />
                      {searchTerm && (
                        <InputGroupAddon align="inline-end">
                          <button
                            onClick={() => setSearchTerm('')}
                            className="text-muted-foreground hover:text-foreground transition-colors p-1"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </InputGroupAddon>
                      )}
                    </InputGroup>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">View:</span>
                      <Button
                        onClick={() => setViewMode('grid')}
                        variant={viewMode === 'grid' ? 'default' : 'ghost'}
                        size="sm"
                        className="flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                          <rect x="3" y="3" width="7" height="7"></rect>
                          <rect x="14" y="3" width="7" height="7"></rect>
                          <rect x="14" y="14" width="7" height="7"></rect>
                          <rect x="3" y="14" width="7" height="7"></rect>
                        </svg>
                        Cards
                      </Button>
                      
                      <Button
                        onClick={() => setViewMode('map')}
                        variant={viewMode === 'map' ? 'default' : 'ghost'}
                        size="sm"
                        className="flex items-center"
                      >
                        <MapIcon />
                        <span className="ml-1">Map</span>
                      </Button>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">Sort by:</span>
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="newest">Recently Added</SelectItem>
                          <SelectItem value="oldest">Oldest First</SelectItem>
                          <SelectItem value="price-high">Price: High to Low</SelectItem>
                          <SelectItem value="price-low">Price: Low to High</SelectItem>
                          <SelectItem value="address">Address A-Z</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 text-sm text-muted-foreground">
                  {searchTerm ? (
                    `Showing ${filteredFavorites.length} of ${favorites.length} favorite properties`
                  ) : (
                    `Showing ${filteredFavorites.length} of ${favorites.length} favorite properties`
                  )}
                  {loadingMissingFavorites && (
                    <div className="mt-2 text-blue-600 flex items-center gap-2">
                      <LoaderIcon className="w-4 h-4" />
                      Loading additional favorite properties...
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {filteredFavorites.length === 0 && searchTerm ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 text-gray-300 mx-auto mb-4">
                    <SearchIcon className="w-16 h-16" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-2">No Matching Favorites</h3>
                  <p className="text-gray-500">
                    No favorite properties match your search for "{searchTerm}"
                  </p>
                  <Button
                    onClick={() => setSearchTerm('')}
                    variant="outline"
                    size="sm"
                    className="mt-4"
                  >
                    Clear Search
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredFavorites.map((property) => (
                      <PropertyCard
                        key={property.roll_number}
                        property={property}
                        onPropertyClick={onPropertyClick}
                        toggleFavorite={toggleFavorite}
                        isFavorite={isFavorite}
                        getPropertyTags={getPropertyTags}
                        onOpenTagManager={onOpenTagManager}
                        hasNote={hasNote}
                      />
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <MapIcon />
                        <span className="ml-2">Favorite Properties Locations</span>
                        <span className="ml-2 text-sm text-gray-500 font-normal">
                          ({filteredFavorites.filter(p => p.centroid_lat && p.centroid_lon).length} properties with coordinates)
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <PropertyMap 
                        properties={filteredFavorites} 
                        isFavorite={isFavorite}
                        favoritesState={favorites}
                        onPropertyClick={onPropertyClick}
                      />
                      <p className="text-xs text-gray-500 mt-3">
                        Click on map markers to see property details. Some properties may not appear if coordinates are unavailable.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesView;